<script lang="ts" setup>
import InputWrapper, { InputWrapperProps } from '@/atoms/InputWrapper.vue';
import BaseInput, { BaseInputProps, InputState } from '@/atoms/BaseInput.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import { watch } from 'vue';
import { ENTER_KEY_HINTS } from '@/constant/inputs';

export type TextInputProps = InputWrapperProps &
  Omit<BaseInputProps, 'type'> & {
    type?: 'text' | 'number' | 'email' | 'password' | 'datetime-local';
    iconName?: string;
    secondIconHint?: string;
    secondIconName?: string;
    onSecondaryIconClick?: () => void;
  };

const props = withDefaults(defineProps<TextInputProps>(), {
  enterkeyhint: ENTER_KEY_HINTS.done,
});
const state = defineModel<InputState>('value');
const value = defineModel<string>();

const onEnter = (e: KeyboardEvent) => {
  if (props.enterkeyhint === ENTER_KEY_HINTS.done) {
    (e.target as HTMLInputElement)?.blur();
  }
};

watch(
  value,
  (newValue) => {
    if (newValue && state.value !== 'disabled') {
      state.value = 'filled';
    }
  },
  { immediate: true },
);

const setFocused = () => {
  if (state.value !== 'disabled') {
    state.value = 'focused';
  }
};

const updateState = () => {
  if (value.value !== '' && state.value !== 'disabled') {
    state.value = 'filled';
  } else if (!value.value || value.value === '') {
    state.value = 'default';
  }
};
</script>

<template>
  <InputWrapper
    :id="id"
    :class="props.class"
    :destructive="destructive"
    :error="error"
    :hint="hint"
    :label="label"
    :state="state"
  >
    <BaseInput
      :id="id"
      :destructive="destructive"
      :field-props="fieldProps"
      :state="state"
      :type="type ?? 'text'"
      placeholder="placeholder"
    >
      <template #left-slot>
        <LocalIcon v-if="iconName" :name="iconName" size="medium" />
      </template>
      <input
        :id="id"
        v-model="value"
        :autocomplete="autocomplete"
        :data-cy="id"
        :disabled="state === 'disabled' || disabled"
        :enterkeyhint="enterkeyhint"
        :max="max"
        :maxlength="maxLength"
        :minlength="minLength"
        :placeholder="placeholder"
        :type="type"
        class="w-full h-full font-inter"
        v-bind="fieldProps"
        @blur="updateState"
        @click="setFocused"
        @focusin="setFocused"
        @keydown.enter="onEnter"
      />
      <template #right-slot>
        <BaseLabel
          v-if="secondIconHint"
          :text="secondIconHint"
          class="ml-2 text-secondary font-medium"
        />
        <LocalIcon
          v-if="secondIconName"
          :name="secondIconName"
          size="small"
          @click="onSecondaryIconClick"
        />
      </template>
    </BaseInput>
  </InputWrapper>
</template>
